/**
 * Reglas de negocio puras para PR (Purchase Requisition) - modelo VD §4.10 + §5.3.
 * Sin dependencias de UI ni backend - facilmente testeables.
 */

import type { Pr, PrLinea, Po, So, Mr, TipoSuministro, LeadTimeClasif } from "@app-types/entities";
import type { EstadoPr } from "@constants/estados";
import { TRANSICIONES_PR, puedeTransicionar } from "@constants/estados";

/** Suma del valor total de las lineas. */
export function calcularMontoLineas(lineas: PrLinea[]): number {
  return lineas.reduce((s, l) => s + l.valorTotal, 0);
}

/** Recalcula valor_total = cantidad * valor_unit en cada linea. */
export function recalcularLineas(lineas: PrLinea[]): PrLinea[] {
  return lineas.map((l) => ({ ...l, valorTotal: round2(l.cantidad * l.valorUnit) }));
}

function round2(n: number): number {
  return Math.round(n * 100) / 100;
}

/** Valida si la transicion de estado es permitida (VD §5.3). */
export function puedeTransicionarPr(actual: EstadoPr, destino: EstadoPr): boolean {
  return puedeTransicionar(actual, destino, TRANSICIONES_PR);
}

/**
 * Determina si los datos estructurales de la PR (concepto, AFE, coste,
 * tipo_suministro, lead_time, disciplina, monto, moneda, lineas) son
 * editables segun el estado.
 *
 * Solo CREADA_NO_APROBADA permite cambiar la "esencia" de la PR. A partir
 * de APROBADA, los cambios estructurales requeririan un proceso de
 * cancelacion + nueva PR, asi que la UI los bloquea para evitar
 * inconsistencias con las POs derivadas y el techo de coste consumido.
 */
export function puedeEditarDatosGeneralesPr(estado: EstadoPr): boolean {
  return estado === "CREADA_NO_APROBADA";
}

/**
 * Determina si los timestamps administrativos de proceso siguen siendo
 * editables. Esto es siempre true mientras la PR este viva (no cancelada
 * ni adjudicada total), porque los timestamps registran el avance del
 * flujo de Abastecimientos a medida que ocurre.
 */
export function puedeEditarTiemposPr(estado: EstadoPr): boolean {
  return estado !== "CANCELADA" && estado !== "ADJ_TOTAL";
}

/**
 * Verifica si una PR puede eliminarse fisicamente.
 *
 * Regla: una PR solo se puede eliminar si NO ha generado POs (es decir,
 * no esta adjudicada parcial ni totalmente). En esos estados las POs
 * derivadas dependen del id de la PR; eliminarla rompe la referencia.
 *
 * Estados eliminables: CREADA_NO_APROBADA, APROBADA, ET_FAC, EE_ABAS,
 * CANCELADA. Estados bloqueados: ADJ_PARCIAL, ADJ_TOTAL.
 *
 * Adicionalmente verifica que no haya POs con prId apuntando a esta PR
 * (defensivo: aunque el estado no sea ADJ_*, podria haber POs).
 */
export function puedeEliminarPr(
  pr: Pick<Pr, "id" | "estado">,
  pos: Po[],
): { puede: boolean; motivo?: string } {
  if (pr.estado === "ADJ_PARCIAL" || pr.estado === "ADJ_TOTAL") {
    return {
      puede: false,
      motivo: `No se puede eliminar una PR en estado ${pr.estado} (ya generó POs).`,
    };
  }
  const posDerivadas = pos.filter((p) => p.prId === pr.id);
  if (posDerivadas.length > 0) {
    return {
      puede: false,
      motivo: `Esta PR tiene ${posDerivadas.length} PO(s) derivadas. Cancela las POs antes de eliminar la PR.`,
    };
  }
  return { puede: true };
}

/** Calcula plazo en dias entre fechaCreacion y fechaLimite. */
export function calcularPlazoDias(fechaCreacion: string, fechaLimite: string): number {
  if (!fechaCreacion || !fechaLimite) return 0;
  const a = new Date(fechaCreacion + "T00:00:00").getTime();
  const b = new Date(fechaLimite + "T00:00:00").getTime();
  if (isNaN(a) || isNaN(b)) return 0;
  return Math.round((b - a) / (1000 * 60 * 60 * 24));
}

/** Validacion completa de la PR (campos obligatorios + consistencia). */
export function validarPr(
  p: Pick<
    Pr,
    | "codigo"
    | "afeId"
    | "costeCodigo"
    | "concepto"
    | "tipoSuministro"
    | "leadTimeClasif"
    | "disciplina"
    | "solicitadoPor"
    | "fechaCreacionPr"
    | "fechaLimite"
    | "monto"
  >,
  lineas: PrLinea[],
): { valido: boolean; errores: string[] } {
  const errores: string[] = [];
  if (!p.codigo?.trim()) errores.push("Código es obligatorio");
  if (!p.afeId) errores.push("AFE asociado es obligatorio");
  if (!p.costeCodigo) errores.push("Clase de coste es obligatoria");
  if (!p.concepto?.trim()) errores.push("Concepto es obligatorio");
  if (!p.tipoSuministro) errores.push("Tipo de suministro es obligatorio");
  if (!p.leadTimeClasif) errores.push("Lead time (LLI / SLI) es obligatorio");
  if (!p.disciplina) errores.push("Disciplina es obligatoria");
  if (!p.solicitadoPor?.trim()) errores.push("Solicitado por es obligatorio");
  if (!p.fechaCreacionPr) errores.push("Fecha de creación es obligatoria");
  if (!p.fechaLimite) errores.push("Fecha límite (lead time) es obligatoria");
  if (
    p.fechaCreacionPr &&
    p.fechaLimite &&
    p.fechaLimite < p.fechaCreacionPr
  ) {
    errores.push("Fecha límite debe ser posterior a la fecha de creación");
  }
  if (!p.monto || p.monto <= 0) errores.push("Monto debe ser mayor a cero");

  for (const l of lineas) {
    if (!l.descripcion?.trim()) {
      errores.push("Toda línea requiere descripción");
      break;
    }
  }
  for (const l of lineas) {
    if (l.cantidad <= 0 || l.valorUnit <= 0) {
      errores.push("Toda línea requiere cantidad y valor unitario mayores a cero");
      break;
    }
  }

  if (lineas.length > 0) {
    const totalLineas = calcularMontoLineas(lineas);
    if (Math.abs(totalLineas - p.monto) > 0.01) {
      errores.push(
        `Suma de líneas (${totalLineas.toFixed(2)}) no coincide con monto del PR (${p.monto.toFixed(2)})`,
      );
    }
  }
  return { valido: errores.length === 0, errores };
}

// ============================================================================
// SLA teórico de gestión PR (Aprobación PR → emisión 1ª PO)
// ============================================================================

/**
 * Plazo teórico de referencia (en días calendario) entre la Aprobación de la PR
 * y la emisión de la primera PO, según el tipo de suministro y la clasificación
 * de lead time.
 *
 * Reglas Tecpetrol Abastecimientos:
 *   - 60 días: MATERIAL+SLI, EQUIPO+SLI, SERVICIO_PUNTUAL
 *   - 90 días: MATERIAL+LLI, EQUIPO+LLI, CONTRATO_MARCO
 *
 * Es plazo de **referencia** (mínimo esperable), no bloquea ni dispara alertas
 * automáticas. Se usa para reportes de gestión y comparativas.
 */
export function slaLimitePr(
  tipoSuministro: TipoSuministro,
  leadTimeClasif: LeadTimeClasif,
): { dias: number; descripcion: string } {
  if (tipoSuministro === "CONTRATO_MARCO") {
    return { dias: 90, descripcion: "Contrato marco" };
  }
  if (tipoSuministro === "SERVICIO_PUNTUAL") {
    return { dias: 60, descripcion: "Servicio puntual" };
  }
  // MATERIAL o EQUIPO — depende de LLI/SLI
  if (leadTimeClasif === "LLI") {
    return {
      dias: 90,
      descripcion: tipoSuministro === "EQUIPO" ? "Equipo LLI" : "Material LLI",
    };
  }
  return {
    dias: 60,
    descripcion: tipoSuministro === "EQUIPO" ? "Equipo SLI" : "Material SLI",
  };
}

/**
 * Calcula los días reales transcurridos entre la aprobación de la PR y la
 * emisión de la 1ª PO. Si aún no hay emisión, usa la fecha "hoy" como cierre
 * provisional (mide el tiempo en curso).
 *
 * Devuelve null si no hay fecha de aprobación (PR todavía en CREADA_NO_APROBADA).
 */
export function diasGestionPr(
  fechaAprobacionGerencia: string | null | undefined,
  fechaEmisionPo: string | null | undefined,
  hoy: Date = new Date(),
): { diasTranscurridos: number; cerrado: boolean } | null {
  if (!fechaAprobacionGerencia) return null;
  const a = new Date(fechaAprobacionGerencia + "T00:00:00").getTime();
  if (isNaN(a)) return null;
  const cerrado = !!fechaEmisionPo;
  const fin = cerrado
    ? new Date(fechaEmisionPo + "T00:00:00").getTime()
    : hoy.getTime();
  if (isNaN(fin)) return null;
  return {
    diasTranscurridos: Math.round((fin - a) / (1000 * 60 * 60 * 24)),
    cerrado,
  };
}

/**
 * Estado SLA: comparación entre días reales y SLA teórico.
 *   - "ok": <= SLA (verde)
 *   - "alerta": SLA < dias <= SLA*1.25 (amarillo, en zona de tolerancia)
 *   - "excedido": > SLA*1.25 (rojo)
 *   - "pendiente": no aplica todavía (no aprobada o sin SLA)
 */
export function estadoSlaPr(
  dias: number,
  slaDias: number,
): "ok" | "alerta" | "excedido" {
  if (dias <= slaDias) return "ok";
  if (dias <= slaDias * 1.25) return "alerta";
  return "excedido";
}

// ============================================================================
// Reglas de techo de coste §7.3 (compartidas entre PR/PO/SO)
// ============================================================================

/**
 * Calcula el consumo agregado por (afeId, costeCodigo).
 * Devuelve el desglose por tipo y el total.
 *
 * Modelo de cómputo (VD §7.4):
 *   - PRs CANCELADA o ADJ_TOTAL → no cuentan como compromiso (la PR ADJ_TOTAL
 *     "desaparece" porque ya fue reemplazada por sus POs derivadas).
 *   - PRs vivas (CREADA_NO_APROBADA, APROBADA, ET_FAC, EE_ABAS, ADJ_PARCIAL)
 *     → cuentan por su **saldo disponible** = PR.monto − Σ(POs no canceladas
 *     derivadas de esa PR). Esto evita el doble conteo cuando una PR
 *     ADJ_PARCIAL ya tiene POs emitidas: el monto comprometido al coste pasa
 *     gradualmente del lado "PR" al lado "PO" a medida que se adjudica.
 *   - POs no canceladas → cuentan por su monto.
 *
 * Resultado: Σ(saldos PR) + Σ(POs vivas) + Σ(SOs) = compromiso real al coste.
 */
/**
 * Calcula el consumo agregado contra un coste de un AFE.
 *
 * Suma pura sobre `montoUsd` (commitment congelado al crear cada entidad
 * con la TRM del AFE en ese momento). No hay conversiones runtime: el USD
 * comprometido al crear una PR/PO/SO es exactamente el USD que se libera
 * al cancelarla, garantizando simetría consume/release.
 */
export function consumoCoste(
  afeId: string,
  costeCodigo: string,
  prs: Pr[],
  pos: Po[],
  sos?: So[],
  mrs?: Mr[],
): { prsActivas: number; pos: number; sos: number; mrs: number; total: number } {
  const prsActivas = prs
    .filter(
      (p) =>
        p.afeId === afeId &&
        p.costeCodigo === costeCodigo &&
        p.estado !== "CANCELADA" &&
        p.estado !== "ADJ_TOTAL",
    )
    .reduce((s, p) => {
      // Saldo en USD = PR.montoUsd − Σ(POs no canceladas derivadas).
      const consumidoPorPos = pos
        .filter((po) => po.prId === p.id && po.estado !== "CANCELADA")
        .reduce((acc, po) => acc + (po.montoUsd ?? 0), 0);
      const saldo = Math.max(0, (p.montoUsd ?? 0) - consumidoPorPos);
      return s + saldo;
    }, 0);

  const posValidas = pos
    .filter(
      (p) =>
        p.afeId === afeId &&
        p.costeCodigo === costeCodigo &&
        p.estado !== "CANCELADA",
    )
    .reduce((s, p) => {
      // NETEO PO→OT, simétrico al PR→PO de arriba.
      //
      // Faltaba, y era una asimetría dentro de esta misma función: la PR sí
      // cedía su saldo a las POs derivadas, pero la PO sumaba íntegra aunque
      // ya se hubieran emitido OT contra ella. El mismo dinero se contaba dos
      // veces —hasta 2× el valor de la PO— y este número es el que alimenta
      // los umbrales del 100 %, +5 % y +10 %: la autoridad del PL y del JEFE
      // se estaba decidiendo sobre una base inflada.
      //
      // Mismo criterio que `saldoPoDisponible`: las OT adicionales NO
      // consumen saldo, son alcance extra que se aprueba aparte.
      const consumidoPorSos = (sos ?? [])
        .filter((so) => so.poId === p.id && so.estado !== "CANCELADA" && !so.esAdicional)
        .reduce((acc, so) => acc + (so.montoUsd ?? 0), 0);
      return s + Math.max(0, (p.montoUsd ?? 0) - consumidoPorSos);
    }, 0);

  // SOs (VD §4.12): cuentan como compromiso al coste igual que las POs.
  // Las SOs CANCELADA no cuentan; las CERRADA siguen contando como gasto
  // ejecutado al coste. Las adicionales sí cuentan (son alcance extra real).
  const sosValidas = (sos ?? [])
    .filter(
      (s) =>
        s.afeId === afeId &&
        s.costeCodigo === costeCodigo &&
        s.estado !== "CANCELADA",
    )
    .reduce((acc, s) => acc + (s.montoUsd ?? 0), 0);

  // MRs: misma lógica que PO/SO pero excluyendo además BORRADOR
  // (la MR aún no emitida no compromete contra el coste).
  const mrsValidas = (mrs ?? [])
    .filter(
      (m) =>
        m.afeId === afeId &&
        m.costeCodigo === costeCodigo &&
        m.estado !== "BORRADOR" &&
        m.estado !== "CANCELADA",
    )
    .reduce((acc, m) => acc + (m.montoUsd ?? 0), 0);

  return {
    prsActivas,
    pos: posValidas,
    sos: sosValidas,
    mrs: mrsValidas,
    total: prsActivas + posValidas + sosValidas + mrsValidas,
  };
}

/** Regla suave §7.3.1: avisa si Σ(PR+PO+SO) > 110% del coste. No bloquea. */
export function reglaSuaveCoste(
  consumoTotal: number,
  valorCoste: number,
  umbralPct = 110,
): { excede: boolean; pct: number; mensaje?: string } {
  if (valorCoste <= 0) return { excede: false, pct: 0 };
  const pct = (consumoTotal / valorCoste) * 100;
  if (pct > umbralPct) {
    return {
      excede: true,
      pct,
      mensaje: `Σ(PR+PO+SO) supera ${umbralPct}% del coste — solicita revisión al PL/JEFE para redistribuir.`,
    };
  }
  return { excede: false, pct };
}

/**
 * Regla dura advertencia §7.3.2: Σ(PO+SO) entre 100% y 110% del coste.
 * Las SO siguen siendo aprobables pero la advertencia queda visible.
 */
export function reglaDuraAdvertencia(
  consumoPoSo: number,
  valorCoste: number,
): { advertir: boolean; pct: number; mensaje?: string } {
  if (valorCoste <= 0) return { advertir: false, pct: 0 };
  const pct = (consumoPoSo / valorCoste) * 100;
  if (pct >= 100 && pct < 110) {
    return {
      advertir: true,
      pct,
      mensaje: `Σ(PO+SO) está en ${pct.toFixed(1)}% del coste — advertencia permanente hasta redistribuir o ampliar AFE.`,
    };
  }
  return { advertir: false, pct };
}

/** Regla dura bloqueo §7.3.3: Σ(PO+SO) ≥ 110% del coste. Bloquea aprobación. */
export function reglaDuraBloqueo(
  consumoPoSo: number,
  valorCoste: number,
): { bloquear: boolean; pct: number; mensaje?: string } {
  if (valorCoste <= 0) return { bloquear: false, pct: 0 };
  const pct = (consumoPoSo / valorCoste) * 100;
  if (pct >= 110) {
    return {
      bloquear: true,
      pct,
      mensaje: `Σ(PO+SO) en ${pct.toFixed(1)}% del coste — bloqueado hasta redistribuir o ampliar AFE.`,
    };
  }
  return { bloquear: false, pct };
}

// ============================================================================
// KPIs de PRs
// ============================================================================

/**
 * Convierte un monto + moneda a USD usando la TRM del AFE.
 * Si no encuentra TRM válida, usa fallback de 4200.
 */
export function montoAUsd(
  monto: number,
  moneda: "USD" | "COP",
  trmAfe: number | undefined,
): number {
  if (moneda === "USD") return monto;
  const trm = trmAfe && trmAfe > 0 ? trmAfe : 4200;
  return monto / trm;
}

/** KPIs para el listado de PRs. */
export function kpisPrs(
  prs: Pr[],
  /** Mapa afeId → TRM del AFE para conversión a USD unificado. Opcional. */
  trmPorAfe?: Map<string, number>,
): {
  total: number;
  creadas: number;
  enProceso: number; // APROBADA + ET_FAC + EE_ABAS + ADJ_PARCIAL
  adjudicadasTotal: number;
  canceladas: number;
  /** Monto agregado por moneda solo de PRs activas. */
  montoPorMoneda: Record<string, number>;
  /** Monto total convertido a USD usando TRM del AFE. */
  montoTotalUsd: number;
} {
  const activas = prs.filter((p) => p.estado !== "CANCELADA" && p.estado !== "ADJ_TOTAL");
  const montoPorMoneda: Record<string, number> = {};
  let montoTotalUsd = 0;
  for (const p of activas) {
    montoPorMoneda[p.moneda] = (montoPorMoneda[p.moneda] ?? 0) + p.monto;
    montoTotalUsd += montoAUsd(p.monto, p.moneda, trmPorAfe?.get(p.afeId));
  }
  return {
    total: prs.length,
    creadas: prs.filter((p) => p.estado === "CREADA_NO_APROBADA").length,
    enProceso: prs.filter(
      (p) =>
        p.estado === "APROBADA" ||
        p.estado === "ET_FAC" ||
        p.estado === "EE_ABAS" ||
        p.estado === "ADJ_PARCIAL",
    ).length,
    adjudicadasTotal: prs.filter((p) => p.estado === "ADJ_TOTAL").length,
    canceladas: prs.filter((p) => p.estado === "CANCELADA").length,
    montoPorMoneda,
    montoTotalUsd: Math.round(montoTotalUsd * 100) / 100,
  };
}
